import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LinkedCircles, DotGrid } from "@/components/Shapes";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !email.includes("@")) {
            toast.error("Please enter a valid email");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/newsletter/subscribe", { email });
            toast.success(
                data.already_subscribed
                    ? "You're already on the list. Thank you."
                    : "You're in. I'll write when there's something worth reading."
            );
            setEmail("");
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <footer className="mt-24" data-testid="site-footer">
            {/* Newsletter band — deep teal, sets it apart from the rest of the site */}
            <section className="bg-[rgb(var(--teal))] text-[rgb(var(--paper))] relative overflow-hidden">
                <div className="absolute top-6 left-6 opacity-30 hidden md:block">
                    <DotGrid rows={5} cols={5} gap={12} size={2.5} color="gold" />
                </div>
                <div className="absolute -bottom-8 -right-6 opacity-40 hidden md:block">
                    <LinkedCircles size={180} color="gold" stroke={1.25} />
                </div>

                <div className="mx-auto max-w-[1280px] px-6 md:px-10 py-16 md:py-20 grid md:grid-cols-12 gap-10 items-start relative z-10">
                    <div className="md:col-span-5">
                        <p className="overline text-[rgb(var(--gold))]">The Letter</p>
                        <h3 className="mt-3 text-2xl md:text-3xl font-bold leading-tight">
                            A quiet note when I write something new.
                        </h3>
                    </div>
                    <div className="md:col-span-7">
                        <p className="text-[15px] leading-relaxed text-[rgb(var(--paper))]/75 max-w-md">
                            No launches. No funnels. Just essays, delivered when they&apos;re ready.
                        </p>
                        <form onSubmit={submit} className="mt-6 flex gap-4 max-w-lg items-end" data-testid="footer-subscribe-form">
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your@email.com"
                                className="flex-1 bg-transparent border-b border-[rgb(var(--paper))]/40 py-2 text-base text-[rgb(var(--paper))] placeholder:text-[rgb(var(--paper))]/45 focus:outline-none focus:border-[rgb(var(--gold))] transition-colors"
                                data-testid="footer-subscribe-email"
                            />
                            <button
                                type="submit"
                                disabled={busy}
                                className="text-[11px] tracking-[0.22em] uppercase font-bold text-[rgb(var(--gold))] border-b border-[rgb(var(--gold))] pb-2 hover:text-[rgb(var(--paper))] hover:border-[rgb(var(--paper))] transition-colors disabled:opacity-50 whitespace-nowrap"
                                data-testid="footer-subscribe-submit"
                            >
                                {busy ? "…" : "Subscribe →"}
                            </button>
                        </form>
                    </div>
                </div>
            </section>

            {/* Bottom navigation band */}
            <div className="border-t hairline bg-[rgb(var(--paper))]">
                <div className="mx-auto max-w-[1280px] px-6 md:px-10 py-14 grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-6">
                        <p className="wordmark text-lg">
                            Overachieving <em>for me</em>
                        </p>
                        <p className="mt-3 text-[14px] text-[rgb(var(--ink-2))] max-w-md">
                            Making sense of the world with equal parts skepticism, humor, and hope.
                        </p>
                    </div>

                    <div className="md:col-span-3">
                        <p className="overline">Read</p>
                        <ul className="mt-4 space-y-2 text-[15px]">
                            <li><Link to="/champagne-and-bagel-bites" className="hover:text-[rgb(var(--orange))]" data-testid="footer-link-cbb">Champagne &amp; Bagel Bites</Link></li>
                            <li><Link to="/overachieving-for-me" className="hover:text-[rgb(var(--orange))]" data-testid="footer-link-ofm">Overachieving for Me</Link></li>
                            <li><Link to="/life-as-an-elective" className="hover:text-[rgb(var(--orange))]" data-testid="footer-link-lae">Life as an Elective</Link></li>
                        </ul>
                    </div>

                    <div className="md:col-span-3">
                        <p className="overline">Elsewhere</p>
                        <ul className="mt-4 space-y-2 text-[15px]">
                            <li><Link to="/about" className="hover:text-[rgb(var(--orange))]" data-testid="footer-link-about">About Leah</Link></li>
                            <li><Link to="/contact" className="hover:text-[rgb(var(--orange))]" data-testid="footer-link-contact">Let&apos;s Create Together</Link></li>
                            <li>
                                <a
                                    href="https://elenemstrategies.com"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="hover:text-[rgb(var(--orange))]"
                                    data-testid="footer-link-elenem"
                                >
                                    Elenem Strategies ↗
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="border-t hairline">
                    <div className="mx-auto max-w-[1280px] px-6 md:px-10 py-6 flex flex-col md:flex-row justify-between gap-2 text-xs text-[rgb(var(--ink-2))]/70">
                        <p>© {new Date().getFullYear()} Leah Moschella.</p>
                        <p>Built for stories, not trophies.</p>
                    </div>
                </div>
            </div>
        </footer>
    );
}
